import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppointment, useCancelAppointment, useRescheduleAppointment } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import {
  AppointmentDetails,
  CancelDialog,
  RescheduleDialog,
} from '@/components/scheduling';

export function AppointmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);

  const { data: appointment, isLoading, isError } = useAppointment(id || '');
  const cancelAppointment = useCancelAppointment();
  const rescheduleAppointment = useRescheduleAppointment();

  const userRole = user?.role === 'DOCTOR' ? 'doctor' : 'patient';

  const handleCancel = () => {
    setCancelDialogOpen(true);
  };

  const handleReschedule = () => {
    setRescheduleDialogOpen(true);
  };

  const handleJoin = () => {
    if (appointment) {
      navigate(`/telemedicine/waiting/${appointment.id}`);
    }
  };

  const handleConfirmCancel = async (reason: string) => {
    if (id) {
      await cancelAppointment.mutateAsync({ appointmentId: id, reason });
      setCancelDialogOpen(false);
    }
  };

  const handleConfirmReschedule = async (newSlotId: string) => {
    if (id) {
      await rescheduleAppointment.mutateAsync({ appointmentId: id, newSlotId });
      setRescheduleDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-3xl py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (isError || !appointment) {
    return (
      <div className="container max-w-3xl py-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">Agendamento não encontrado</h2>
          <p className="text-gray-500 mt-2">
            O agendamento que você está procurando não existe ou foi removido.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-primary-600 hover:text-primary-700"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar
      </button>

      <AppointmentDetails
        appointment={appointment}
        userRole={userRole}
        onCancel={handleCancel}
        onReschedule={handleReschedule}
        onJoin={handleJoin}
        isLoading={cancelAppointment.isPending || rescheduleAppointment.isPending}
      />

      <CancelDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        isLoading={cancelAppointment.isPending}
        appointmentDate={appointment.scheduledAt}
      />

      {appointment.doctor && (
        <RescheduleDialog
          isOpen={rescheduleDialogOpen}
          onClose={() => setRescheduleDialogOpen(false)}
          onConfirm={handleConfirmReschedule}
          isLoading={rescheduleAppointment.isPending}
          doctorId={appointment.doctor.id}
          currentDate={appointment.scheduledAt}
        />
      )}
    </div>
  );
}
